import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type ReactPlayerType from "react-player/file";
import dynamic from "next/dynamic";
import formatDuration from "format-duration";
import { CgArrowsExpandRight } from "react-icons/cg";
import { FaPause, FaPlay, FaVolumeMute, FaVolumeUp } from "react-icons/fa";
import { preventDefault } from "src/libs/event-lib";
import { requestFullscreen } from "src/libs/fullscreen";
import { ProgressBar } from "./ProgressBar";
import { isDesktopSafari } from "src/libs/browser-lib";
import { cn } from "src/utils/cn";
import { VideoSource } from "src/libs/segment-lib";
import { isBrowser } from "src/libs/nextLib";
import { useRotationScaleToFit } from "src/hooks/useRotationScaleToFit";

// ReactPlayer is causing server-side and client-side renders to be different
// (hydration error). Until that's fixed, we're just opting it out of SSR. C.f.:
// - https://github.com/cookpete/react-player/issues/1474
// - https://github.com/emotion-js/emotion/issues/2926
// - https://github.com/cookpete/react-player/issues/1455#issuecomment-1207154843
const ReactPlayer = dynamic(
  () => import("@/src/components/VideoPlayer/ReactPlayerWrapper"),
  {
    ssr: false,
  }
);

// A progress change greater than this, between updates, is
// considered to be a user seek
const MINIMUM_SEEK_FRACTION = 0.05;

export type VideoControl = "mute" | "fullscreen" | "download";

type ControlButtonProps = {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

function ControlButton({
  children,
  onClick,
  disabled = false,
  ...props
}: ControlButtonProps) {
  return (
    <button
      className="cursor-pointer rounded-sm p-2 drop-shadow-[0_0_5px_#1b5e20] hover:bg-red-500 disabled:cursor-not-allowed disabled:hover:bg-red-200"
      onClick={preventDefault(onClick)}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}

function includeNegativeSign(playRemainingSeconds: number) {
  return playRemainingSeconds >= 1;
}

function PreviewPlayIcon() {
  return (
    <div
      className={cn(
        "z-10 flex justify-center transition-transform group-hover:scale-125",
        isDesktopSafari() ? null : `drop-shadow-lg`
      )}
    >
      <FaPlay className="relative w-[5%] min-w-[20px] text-white" size="100%" />
    </div>
  );
}

function ThumbImage({
  src,
  style,
}: {
  src: string;
  style: React.CSSProperties;
}) {
  return (
    <img
      src={src}
      className="absolute h-full w-full object-contain"
      alt=""
      style={style}
    />
  );
}

type VideoPlayerProps = {
  sources: VideoSource[];
  rotate?: {
    rotateDegrees: number;
    width: number;
    height: number;
  };
  thumbUrl?: string;
  // immediately start playing when the preview thumbnail is clicked
  playOnClick?: boolean;
  controls?: VideoControl[];
  compact?: boolean;
  trim?: {
    start: number;
    end: number;
    cut: boolean;
  };
  onPlay?: () => void;
  onPause?: () => void;
};

export function VideoPlayer({
  sources,
  thumbUrl,
  rotate,
  playOnClick = true,
  controls = ["mute", "fullscreen"],
  compact = false,
  trim,
  onPlay,
  onPause,
}: VideoPlayerProps) {
  const hasPreview = Boolean(thumbUrl);

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ReactPlayerType | null>(null);
  const [isReady, setIsReady] = useState(!hasPreview);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasNotPlayed, setHasNotPlayed] = useState(true);
  const [isPreviewShowing, setIsPreviewShowing] = useState(hasPreview);
  // duration in seconds (could be a fraction)
  const [duration, setDuration] = useState(0);
  // a number between 0 and 1
  const [playProgress, setPlayProgress] = useState(0);
  // how far we've played, in seconds (could be a fraction)
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [muted, setMuted] = useState(false);

  function getInternalPlayer() {
    if (!playerRef.current) {
      return;
    }

    const playerEl = playerRef.current.getInternalPlayer();

    if (!(playerEl instanceof Element)) {
      return;
    }

    return playerEl;
  }

  const rotationScale = useRotationScaleToFit(
    containerRef,
    { width: rotate?.width ?? 0, height: rotate?.height ?? 0 },
    rotate?.rotateDegrees ?? 0
  );

  const rotateStyle = useMemo(() => {
    if (rotate?.rotateDegrees === undefined) {
      return { transform: "" };
    }

    return {
      transform: `rotate(${rotate.rotateDegrees}deg) scale(${rotationScale})`,
    };
  }, [rotate?.rotateDegrees, rotationScale]);

  // There's no way to transform the video element using props, so we have to
  // escape hatch to the DOM and set it directly.
  useEffect(() => {
    if (!playerRef.current || !isReady) {
      return;
    }

    const playerEl = playerRef.current.getInternalPlayer();

    if (!(playerEl instanceof HTMLVideoElement)) {
      return;
    }

    playerEl.style.transform = rotateStyle.transform;
  }, [
    rotateStyle,
    // we want to re-render on isReady because we'll know that playerRef is set
    // up by then
    isReady,
  ]);

  // If useSWR re-fetches a card/compilation/clip/etc, it will be different
  // as far as react is concerned (even if no values changed). That will cause
  // `sources` to change and will cause the player to reset. So instead of
  // checking sources using object equivalence, let's use value equivalence.
  const sourcesStr = useMemo(() => {
    return JSON.stringify(sources);
  }, [sources]);

  const controlsStr = useMemo(() => {
    return JSON.stringify(controls);
  }, [controls]);

  const sourcesLocked = useMemo(() => sources, [sourcesStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const controlsLocked = useMemo(() => controls, [controlsStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMute = useMemo(
    () => controlsLocked.includes("mute"),
    [controlsLocked]
  );

  const hasFullscreen = useMemo(
    () => controlsLocked.includes("fullscreen"),
    [controlsLocked]
  );

  const canDownload = useMemo(
    () => controlsLocked.includes("download"),
    [controlsLocked]
  );

  const playRemaining = useMemo(() => {
    return duration - playedSeconds;
  }, [duration, playedSeconds]);

  const playRemainingStr = useMemo(() => {
    return includeNegativeSign(playRemaining)
      ? "-" + formatDuration(playRemaining * 1000)
      : "0:00";
  }, [playRemaining]);

  // these useCallbacks are necessary to prevent the player from resetting on
  // certain state changes (like window resizes, for some reason)
  const handleSeek = useCallback((progress: number) => {
    if (!playerRef.current) {
      return;
    }
    playerRef.current.seekTo(progress, "fraction");
  }, []);

  const handleReady = useCallback(() => {
    if (!isReady && playerRef.current) {
      // Seeking will invoke onReady, so we do this to avoid a feedback loop
      setIsReady(true);

      if (trim?.cut && trim.start > 0) {
        // If a trim is applied, then start the video
        // at the trim start
        handleSeek(trim.start);
        setIsPlaying(true);
      }
    }

    if (playerRef.current) {
      setDuration(playerRef.current.getDuration() || 0);
    }
  }, [setDuration, trim, handleSeek, isReady]);

  const handleEnded = useCallback(() => {
    if (trim?.cut && trim.start > 0) {
      // If a trim is applied, then reset the video
      // to the trim start
      handleSeek(trim.start);
    }
  }, [trim, handleSeek]);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    onPlay?.();
  }, [setIsPlaying, onPlay]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPause?.();
  }, [setIsPlaying, onPause]);

  const handleProgress = useCallback(
    ({ played, playedSeconds }: { played: number; playedSeconds: number }) => {
      if (
        isPlaying &&
        trim?.cut &&
        played >= trim.end &&
        playProgress < trim.end &&
        played - playProgress < MINIMUM_SEEK_FRACTION
      ) {
        // If the video is playing and we've crossed the trim.end boundary
        // naturally (e.g. not a seek), then pause the video and return to
        // the start (as is done for a non-trimmed video).
        setIsPlaying(false);
        handleSeek(trim.start);
      }

      setPlayProgress(played);
      setPlayedSeconds(playedSeconds);
    },
    [setPlayProgress, playProgress, trim, handleSeek, isPlaying]
  );

  // This check is not reactive, but since the video player re-renders on every frame,
  // it doesn't matter; the component will still update when the fullscreen state changes.
  function getIsFullscreen(): boolean {
    // Prevent document access on server-side rendering
    return isBrowser() && Boolean(document.fullscreenElement);
  }

  const handleFullScreen = () => {
    if (!hasFullscreen) {
      return;
    }

    const playerEl = getInternalPlayer();

    if (!playerEl) {
      return;
    }

    requestFullscreen(playerEl);
  };

  const handleClickPreview = useCallback(() => {
    setIsPreviewShowing(false);

    if (hasPreview && playOnClick) {
      setIsPlaying(true);
    }
  }, [setIsPlaying, hasPreview, playOnClick]);

  const toggleMute = () => {
    setMuted((x) => !x);
  };

  const togglePlay = () => {
    setIsPlaying((x) => !x);
    setHasNotPlayed(false);
  };

  const buttonSize = compact ? 16 : 20;

  return (
    <div ref={containerRef} className="VideoPlayer relative h-full w-full">
      {thumbUrl && hasNotPlayed ? (
        <ThumbImage src={thumbUrl} style={rotateStyle} />
      ) : null}
      {!isPreviewShowing && isReady ? (
        <div className="absolute bottom-0 z-10 flex w-full items-center gap-2 p-1 text-white">
          <ControlButton
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
          >
            {isPlaying ? (
              <FaPause size={buttonSize} />
            ) : (
              <FaPlay size={buttonSize} />
            )}
          </ControlButton>
          <div className="grow">
            <ProgressBar
              progress={playProgress}
              onSeek={handleSeek}
              isPlaying={isPlaying}
              play={() => setIsPlaying(true)}
              pause={() => setIsPlaying(false)}
            />
          </div>
          <div>
            {/* Normally playRemainingStr has a negative sign, except at the very
             * end of play, when playRemainingStr is "0:00". When that happens,
             * the playRemainingStr length becomes smaller, which causes the
             * play bar to become longer (because it uses up all the extra space
             * on this line), which causes some jank in the position of the
             * player head. So we just need to add a bit of extra space to
             * replace the missing negative sign.
             */}
            {!includeNegativeSign(playRemaining) ? <span>&nbsp;</span> : null}
            {playRemainingStr}
          </div>
          {hasMute ? (
            <ControlButton
              aria-label={muted ? "Unmute" : "Mute"}
              onClick={toggleMute}
            >
              {muted ? (
                <FaVolumeMute size={buttonSize} />
              ) : (
                <FaVolumeUp size={buttonSize} />
              )}
            </ControlButton>
          ) : null}
          {hasFullscreen ? (
            <ControlButton aria-label="Full screen" onClick={handleFullScreen}>
              <CgArrowsExpandRight size={buttonSize} />
            </ControlButton>
          ) : null}
        </div>
      ) : null}
      <ReactPlayer
        // `aspect-ratio` css property does not work on ios 14 safari
        // Hence `padding-top[56.25%]` is added to achieve 16:9 aspect ratio
        className="video-player-container group relative h-full w-full pt-[56.25%]"
        playerRef={playerRef}
        width="100%"
        height="100%"
        playing={isPlaying}
        url={sourcesLocked}
        // Some browsers (Firefox) will not show system controls when in full screen if this is false.
        // So we just explicitly enable them in full screen.
        controls={getIsFullscreen()}
        muted={muted}
        light={
          thumbUrl ? <ThumbImage src={thumbUrl} style={rotateStyle} /> : false
        }
        // this needs to be relatively frequent or the progress indicator will
        // seem to randomly shift forward on pause (because the player was
        // paused just before the next progress callback)
        progressInterval={20}
        onReady={handleReady}
        onClickPreview={handleClickPreview}
        onPlay={handlePlay}
        onPause={handlePause}
        onProgress={handleProgress}
        onEnded={handleEnded}
        playsinline
        playIcon={<PreviewPlayIcon />}
        config={
          {
            file: {
              attributes: {
                onContextMenu: (e: MouseEvent) => {
                  if (!canDownload) {
                    // this will prevent the context menu (e.g. "right-click
                    // menu" from showing up)
                    // c.f. https://github.com/cookpete/react-player/issues/551#issuecomment-454779788
                    e.preventDefault();
                  }
                },
              },
            },
            // the file.attributes.onContextMenu props is not set for the config
            // type, so we get a TS error without setting config to `any`.
          } as any
        }
      />
    </div>
  );
}
